import { FC, useEffect } from 'react'
import shallow from 'zustand/shallow'
import { useAuth } from '../../context/auth-context'
import {
  EmployeeDisplayTinyInfo,
  ObjectiveDto,
  Currency,
  Country,
  MeetingDashboardFragment,
  SkillEmployeeStatusDto,
  ObjectiveListAndEmployeeContractQuery,
} from '../../types'
import { filterObjectivesByTypeStatusAndRefYear } from '../appraisal/okr/util'
import { ContentContainer } from '../../components/custom_components/ContentContainer'
import { EmployeeTargetsComponent } from './EmployeeTargetsComponent'
import PerSpacer from '../../components/custom_components/Spacer'
import { useTargetsModalStore } from '../../components/custom_components/TargetsModal/TargetsModalStore'
import { TargetModalType } from '../../components/custom_components/TargetsModal/types'
import { TargetsModal } from '../../components/custom_components/TargetsModal/TargetsModal'
import { useObjectiveDTOListAndEmployeeContract } from '../../graphql/objectives/useObjectivesAndEmployeeContract'
import { retrieveCurrencyConversionRate } from '../../shared/currencyConversionUtil'
import { useCurrency } from '../../context/currency-context'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { useTranslation } from 'react-i18next'

type EmployeeTargetsContainerProps = {
  employee?: EmployeeDisplayTinyInfo
  publicProfile?: boolean
  employeeEditable?: boolean
  resignedView?: boolean
  countryOfEmployee?: Country
  plannedMeeting?: MeetingDashboardFragment
  view: 'ResignedEmployeeProfilePage' | 'OwnGrowthPage' | 'PublicProfilePage'
  employeeSkillStatus: SkillEmployeeStatusDto | null
}

type EmployeeTargetsContainerContentProps = {
  data: ObjectiveListAndEmployeeContractQuery
  employee?: EmployeeDisplayTinyInfo
  publicProfile?: boolean
  employeeEditable?: boolean
  resignedView?: boolean
  countryOfEmployee?: Country
  plannedMeeting?: MeetingDashboardFragment
  employeeSkillStatus: SkillEmployeeStatusDto | null
  view: 'ResignedEmployeeProfilePage' | 'OwnGrowthPage' | 'PublicProfilePage'
}

export const EmployeeTargetsContainer: FC<EmployeeTargetsContainerProps> = ({
  employee,
  publicProfile,
  employeeEditable,
  resignedView,
  countryOfEmployee,
  plannedMeeting,
  view,
  employeeSkillStatus,
}) => {
  const { user } = useAuth()
  const { t } = useTranslation()

  const employeeId = employee ? employee.id : user.employeeId
  const { loading, error, data } = useObjectiveDTOListAndEmployeeContract(employeeId, resignedView)

  if (loading || error || !data || !data.objectivesDtoList) {
    return (
      <BackgroundBox r height={view === 'OwnGrowthPage' ? (!!plannedMeeting ? 'withProfileHeaderAndTabs' : 'withTabs') : 'profileView'}>
        <NewLoadingAndErrors isLoading={loading} error={error} hasNoData={!data || !data.objectivesDtoList} showSpinner t={t} />
      </BackgroundBox>
    )
  } else {
    return (
      <EmployeeTargetsContainerContent
        data={data}
        employee={employee}
        publicProfile={publicProfile}
        employeeEditable={employeeEditable}
        resignedView={resignedView}
        countryOfEmployee={countryOfEmployee}
        plannedMeeting={plannedMeeting}
        employeeSkillStatus={employeeSkillStatus}
        view={view}
      />
    )
  }
}

const EmployeeTargetsContainerContent: FC<EmployeeTargetsContainerContentProps> = ({
  data,
  employee,
  publicProfile,
  employeeEditable,
  resignedView,
  employeeSkillStatus,
  countryOfEmployee,
  plannedMeeting,
  view,
}) => {
  const { user } = useAuth()
  const [setModalProps, openTargetModal, cleanModalProps] = useTargetsModalStore(
    (state) => [state.setModalProps, state.openTargetModal, state.cleanModalProps],
    shallow,
  )

  const { currencyConversionData } = useCurrency()

  const employeeId = employee ? employee.id : user.employeeId
  const objectives: ObjectiveDto[] = data.objectivesDtoList
  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, true)
  const devObjectives = filterObjectivesByTypeStatusAndRefYear(objectives, false)

  const currency = !!resignedView
    ? !!data.resignedContract
      ? data.resignedContract.currencyForPayments
      : Currency.Eur
    : !!data.currentContract
    ? data.currentContract.currencyForPayments
    : Currency.Eur

  useEffect(() => {
    setModalProps({ employeeId, publicProfile, employeeCurrency: currency, employeeCountry: countryOfEmployee })
    return () => {
      cleanModalProps()
    }
  }, [employeeId, setModalProps, cleanModalProps, publicProfile, currency, countryOfEmployee])

  const handleOpenModal = (objective: ObjectiveDto) => {
    if (resignedView) {
      openTargetModal(
        TargetModalType.resignedEmployeViewTarget,
        objective,
        !!objective.currency ? retrieveCurrencyConversionRate(objective.currency, currency, currencyConversionData) : undefined,
      )
    } else {
      openTargetModal(
        TargetModalType.viewTarget,
        objective,
        !!objective.currency ? retrieveCurrencyConversionRate(objective.currency, currency, currencyConversionData) : undefined,
      )
    }
  }

  return (
    <>
      <ContentContainer>
        <PerSpacer mb="m">
          <EmployeeTargetsComponent
            monetary
            objectives={monetaryObjectives}
            handleObjectiveClicked={(_, objective) => handleOpenModal(objective)}
            publicProfile={publicProfile}
            employeeIsEditable={employeeEditable}
            employee={employee}
            resignedView={resignedView}
            currency={currency}
            plannedMeeting={plannedMeeting}
            currencyConversionData={currencyConversionData}
            employeeSkillStatus={employeeSkillStatus}
            view={view}
          />
        </PerSpacer>
        <EmployeeTargetsComponent
          objectives={devObjectives}
          handleObjectiveClicked={(_, objective) => handleOpenModal(objective)}
          publicProfile={publicProfile}
          employeeIsEditable={employeeEditable}
          employee={employee}
          resignedView={resignedView}
          plannedMeeting={plannedMeeting}
          employeeSkillStatus={employeeSkillStatus}
          view={view}
        />
      </ContentContainer>
      <TargetsModal />
    </>
  )
}
